<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="review-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.push('/scholar-queue')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <div v-if="isLoading" class="loading-state">{{ $t('common.loading') }}</div>

      <template v-else-if="caseData">
        <h1 class="page-title">
          {{ $t('review.title') }}: {{ caseData.case_number }}
        </h1>

        <div class="review-layout">
          <!-- Left Column: Case Info + Auto Calculation -->
          <div class="review-left">
            <!-- Case Summary -->
            <IntercomCard class="review-card">
              <h3>{{ $t('review.caseInfo') }}</h3>
              <div class="info-rows">
                <div class="info-row">
                  <span class="info-label">{{ $t('case.deceasedName') }}</span>
                  <span class="info-value">{{ caseData.deceased_name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.deceasedGender') }}</span>
                  <span class="info-value">{{ caseData.deceased_gender }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.school') }}</span>
                  <span class="info-value">{{ caseData.school }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.heirs') }}</span>
                  <span class="info-value">{{ caseData.heirs?.length || 0 }} heirs</span>
                </div>
                <div class="info-row" v-if="caseData.estate_value">
                  <span class="info-label">{{ $t('calculator.estateValue') }}</span>
                  <span class="info-value">{{ formatCurrency(caseData.estate_value) }}</span>
                </div>
                <div class="info-row" v-if="caseData.notes">
                  <span class="info-label">{{ $t('case.notes') }}</span>
                  <span class="info-value notes-text">{{ caseData.notes }}</span>
                </div>
              </div>
            </IntercomCard>

            <!-- Auto-Calculated Result -->
            <IntercomCard class="review-card">
              <h3>{{ $t('review.autoCalculation') }}</h3>
              <div v-if="isLoadingCalc">{{ $t('common.loading') }}</div>
              <CalculationResult v-else-if="calculation" :result="calculation" />
              <p v-else class="empty-text">{{ $t('result.noShares') }}</p>
            </IntercomCard>
          </div>

          <!-- Right Column: Review Panel -->
          <div class="review-right">
            <IntercomCard class="review-card">
              <ScholarReviewPanel
                :existing-shares="calculationShares"
                :is-submitting="isSubmitting"
                @submit="handleReviewSubmit"
              />
            </IntercomCard>
          </div>
        </div>
      </template>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import CalculationResult from '../components/CalculationResult.vue'
import ScholarReviewPanel from '../components/ScholarReviewPanel.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { navSections } = useNavigation()
const {
  getCase,
  getCaseCalculation,
  approveCase,
  adjustCase,
  overrideCase,
  requestInfo,
} = useFaraid()

const caseData = ref(null)
const calculation = ref(null)
const isLoading = ref(true)
const isLoadingCalc = ref(false)
const isSubmitting = ref(false)

const calculationShares = computed(() => calculation.value?.shares || [])

const formatCurrency = (value) => {
  if (!value && value !== 0) return ''
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value)
}

const fetchData = async () => {
  isLoading.value = true
  try {
    const response = await getCase(route.params.id)
    caseData.value = response.case || response

    // Also fetch calculation
    isLoadingCalc.value = true
    try {
      const calcResponse = await getCaseCalculation(route.params.id)
      calculation.value = calcResponse.calculation || calcResponse
    } catch {
      // No calculation yet is fine
    } finally {
      isLoadingCalc.value = false
    }
  } catch (error) {
    console.error('Failed to fetch case:', error)
    toast.error(t('error.fetchFailed'))
  } finally {
    isLoading.value = false
  }
}

const handleReviewSubmit = async (payload) => {
  isSubmitting.value = true
  try {
    const caseId = route.params.id
    const scholarId = user.value?.profile_id

    if (payload.mode === 'approve') {
      await approveCase(caseId, {
        scholar_id: scholarId,
        request: {
          reasoning: payload.reasoning,
          notes: payload.notes,
        },
      })
    } else if (payload.mode === 'adjust') {
      await adjustCase(caseId, {
        scholar_id: scholarId,
        request: {
          shares: payload.shares,
          reasoning: payload.reasoning,
          notes: payload.notes,
        },
      })
    } else if (payload.mode === 'override') {
      await overrideCase(caseId, {
        scholar_id: scholarId,
        request: {
          shares: payload.shares,
          reasoning: payload.reasoning,
          notes: payload.notes,
        },
      })
    } else if (payload.mode === 'request_info') {
      await requestInfo(caseId, {
        scholar_id: scholarId,
        request: {
          questions: payload.questions,
          notes: payload.notes,
        },
      })
    }

    toast.success(t('success.reviewSubmitted'))
    router.push('/scholar-queue')
  } catch (error) {
    toast.error(error.message || t('error.saveFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchData()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.review-view {
  padding: var(--space-8);
  max-width: 1400px;
  margin: 0 auto;
}

.loading-state {
  text-align: center;
  padding: var(--space-10);
  color: var(--color-gray-500);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: var(--space-4) 0 var(--space-6) 0;
}

/* Two-column layout */
.review-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-6);
  align-items: start;
}

.review-card {
  padding: var(--space-5);
  margin-bottom: var(--space-5);
}

.review-card h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.info-rows {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-gray-100);
  font-size: var(--font-size-sm);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: var(--color-gray-500);
}

.info-value {
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.notes-text {
  max-width: 300px;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-4);
}

@media (max-width: 1024px) {
  .review-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .review-view {
    padding: var(--space-4);
  }
}
</style>
